import { useTranslation } from "react-i18next";

type AttributesValidationLogicType =
  | "input"
  | "inputNumber"
  | "switch"
  | "select";
export type ContextType = "beacon" | "scb-one";

export interface AttributesValidationLogic {
  type: AttributesValidationLogicType;
  required: boolean;
  translation: string;
  pattern?: RegExp;
  message?: string;
  context?: ContextType[];
}

export const useAttributesValidationLogic = (): Record<
  string,
  AttributesValidationLogic
> => {
  const { t } = useTranslation();

  return {
    activityInterval: {
      type: "inputNumber",
      required: true,
      pattern: /^(?:[0-9]|1[0-9]|20)$/,
      message: t("beacon:configure-number-validation", {
        min: 0,
        max: 20,
      }),
      translation: "beacon:configure-activity-interval",
      context: ["beacon"],
    },
    inactivityInterval: {
      type: "inputNumber",
      required: true,
      pattern: /^(?:10080|100[0-7][0-9]|10080|[1-9][0-9]{0,3}|0)$/,
      message: t("beacon:configure-number-validation", {
        min: 0,
        max: 10080,
      }),
      translation: "beacon:configure-inactivity-interval",
      context: ["beacon"],
    },
    heartbeat: {
      type: "inputNumber",
      required: true,
      pattern: /^(?:1440|1[0-3][0-9]{2}|14[0-3][0-9]|[1-9][0-9]{0,2}|0)$/,
      message: t("beacon:configure-number-validation", {
        min: 0,
        max: 1440,
      }),
      translation: "beacon:configure-heartbeat-interval",
      context: ["beacon"],
    },
    ignoreMotion: {
      type: "inputNumber",
      required: true,
      pattern: /^(?:120|[1-9][0-9]?|1[0-1][0-9])$/,
      message: t("beacon:configure-number-validation", {
        min: 1,
        max: 120,
      }),
      translation: "beacon:configure-ignore-motion",
      context: ["beacon"],
    },
    "accelerometer.duration": {
      type: "inputNumber",
      required: true,
      pattern: /^(?:16300|1[0-5][0-9]{3}|16[0-2][0-9]{2}|[1-9][0-9]{0,3}|0)$/,
      message: t("beacon:configure-number-validation", {
        min: 0,
        max: 16300,
      }),
      translation: "beacon:configure-accelerometer-duration",
      context: ["beacon"],
    },
    "accelerometer.threshold": {
      type: "inputNumber",
      required: true,
      pattern: /^(?:1000|[1-9][0-9]{0,2}|0)$/,
      message: t("beacon:configure-number-validation", {
        min: 0,
        max: 1000,
      }),
      translation: "beacon:configure-accelerometer-threshold",
      context: ["beacon"],
    },
    "accelerometer.x": {
      type: "switch",
      required: true,
      translation: "beacon:configure-accelerometer-x",
      context: ["beacon"],
    },
    "accelerometer.y": {
      type: "switch",
      required: true,
      translation: "beacon:configure-accelerometer-y",
      context: ["beacon"],
    },
    "accelerometer.z": {
      type: "switch",
      required: true,
      translation: "beacon:configure-accelerometer-z",
      context: ["beacon"],
    },
    "positioning.heartbeat": {
      type: "inputNumber",
      required: true,
      pattern: /^(?:[2-9]|[1-5][0-9]|60)$/,
      message: t("beacon:configure-number-validation", {
        min: 2,
        max: 60,
      }),
      translation: "beacon:configure-positioning-heartbeat",
      context: ["beacon", "scb-one"],
    },
    "positioning.ttffTimeout": {
      type: "inputNumber",
      required: true,
      pattern: /^(?:[0-9]|[1-5][0-9]|60)$/,
      message: t("beacon:configure-number-validation", {
        min: 0,
        max: 60,
      }),
      translation: "beacon:configure-positioning-ttffTimeout",
      context: ["beacon", "scb-one"],
    },
    "positioning.ttffRetries": {
      type: "inputNumber",
      required: true,
      pattern: /^[2-5]$/,
      message: t("beacon:configure-number-validation", {
        min: 2,
        max: 5,
      }),
      translation: "beacon:configure-positioning-ttffRetries",
      context: ["beacon", "scb-one"],
    },
    "positioning.fixQualityTimeout": {
      type: "inputNumber",
      required: true,
      pattern: /^(?:1800|1[0-7][0-9]{2}|[1-9][0-9]{0,2}|0)$/,
      message: t("beacon:configure-number-validation", {
        min: 0,
        max: 1800,
      }),
      translation: "beacon:configure-positioning-fixQualityTimeout",
      context: ["beacon", "scb-one"],
    },
    "positioning.dynamicModel": {
      type: "select",
      required: true,
      translation: "beacon:configure-positioning-dynamicModel",
      context: ["beacon", "scb-one"],
    },
    "rtk.credentials.host": {
      type: "input",
      required: true,
      translation: "beacon:configure-rtk-host",
      context: ["beacon", "scb-one"],
    },
    "rtk.credentials.port": {
      type: "input",
      required: true,
      pattern: /^-?\d+$/,
      message: t("number"),
      translation: "beacon:configure-rtk-port",
      context: ["beacon", "scb-one"],
    },
    "rtk.credentials.mountpoint": {
      type: "input",
      required: true,
      translation: "beacon:configure-rtk-mountpoint",
      context: ["beacon", "scb-one"],
    },
    "rtk.credentials.user": {
      type: "input",
      required: true,
      translation: "beacon:configure-rtk-username",
      context: ["beacon", "scb-one"],
    },
    "rtk.credentials.password": {
      type: "input",
      required: true,
      translation: "beacon:configure-rtk-password",
      context: ["beacon", "scb-one"],
    },
    "continuousPositioning.refreshSchedule": {
      type: "input",
      required: true,
      pattern: /^[0-4]$/,
      message: t("beacon:configure-number-validation", {
        min: 0,
        max: 4,
      }),
      translation: "beacon:configure-continuous-refresh-schedule",
      context: ["scb-one"],
    },
    "continuousPositioning.refreshTimeout": {
      type: "input",
      required: true,
      pattern: /^(?:[5-9]|1[0-5])$/,
      message: t("beacon:configure-number-validation", {
        min: 5,
        max: 15,
      }),
      translation: "beacon:configure-continuous-refresh-timeout",
      context: ["scb-one"],
    },
    "continuousPositioning.maxSamples": {
      type: "input",
      required: true,
      pattern: /^(?:[1-9]|1[0-9]|2[0-9]|30)$/,
      message: t("beacon:configure-number-validation", {
        min: 1,
        max: 30,
      }),
      translation: "beacon:configure-continuous-max-samples",
      context: ["scb-one"],
    },
  };
};
